/**
 * General (consumer) discount detection. A product/line is discounted when its
 * pre-discount price is strictly greater than the price actually charged. This
 * mirrors the Webshop-frontend reference logic (list vs. calculated price for
 * products; unit-price vs. without-adjustments for cart lines).
 *
 * B2B "VAT 0%" pricing is handled separately via `cart.isB2b` and is not a
 * general discount.
 */

import type { CartLine } from "@/types/cart";

/**
 * Minimal shape shared by `ProductPrice` (layout) and `ItemCardPrice` (content):
 * a discount is derived purely from the list vs. calculated price pair.
 */
interface DiscountablePrice {
  listPrice?: number | null;
  calculatedPrice?: number | null;
}

/** True when the product's list price is above the (discounted) calculated price. */
export function isProductDiscounted(
  price: DiscountablePrice | null | undefined
): boolean {
  if (!price) return false;
  const { listPrice, calculatedPrice } = price;
  return (
    listPrice != null && calculatedPrice != null && listPrice > calculatedPrice
  );
}

/** True when the cart line's pre-adjustment unit price is above the charged unit price. */
export function isLineDiscounted(line: CartLine): boolean {
  return line.unitPriceWithoutAdjustments > line.unitPrice;
}
